/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Table, Button, Checkbox, message, Input, Upload, Row, Col, Alert, Grid,
} from 'antd';
import { UploadOutlined, CloseOutlined } from '@ant-design/icons';
import {
  DragDropContext, Droppable, Draggable,
} from '@hello-pangea/dnd';
import { useSelector, useDispatch } from 'react-redux';
import { useSocketContext } from '../../sockets/SocketContext';
import { getDocumentStatusDisplay } from '../../utils/helpers';

import FavoriteCard     from './ext/FavoriteCard';      // ТЗ
import DocumentModal    from '../TZ/ext/DocumentModal';
import RequestModal     from '../TZ/ext/RequestModal';
import WipoModal        from '../TZ/WIPO/WipoModal';

import {
  setFavoriteInteresting,
  setLastImageForReport,
} from '../../store/tz/reducer';
import { setReportGenerating, setReportError, setLastSearchQuery as setLastSearchQueryAction } from '../../store/tz/reducer';

const REPORT_INPUT_WIDTH = 240;

const { useBreakpoint } = Grid;

// ────────────────────────────────────────────────────────────────────────────
// Draggable-row
const DraggableBodyRow = (props) => {
  const {
    index, className, style, ...rest
  } = props;

  // Safety check - if index is undefined, return regular row without drag functionality
  if (typeof index !== 'number' || isNaN(index)) {
    return (
      <tr className={className} style={style}>
        {rest.children}
      </tr>
    );
  }

  return (
    <Draggable draggableId={String(rest['data-row-key'])} index={index}>
      {(prov, snap) => (
        <tr
          ref={prov.innerRef}
          {...prov.draggableProps}
          {...prov.dragHandleProps}
          className={className}
          style={{
            ...style,
            background: snap.isDragging ? 'var(--theme-preview-bg)' : 'var(--theme-surface)',
            ...prov.draggableProps.style,
          }}
        >
          {rest.children}
        </tr>
      )}
    </Draggable>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Основной компонент
const FavoritesTab = () => {
  const screens = useBreakpoint();
  const stackReportToolbar = !screens.md;
  const reduxFavorites     = useSelector((s) => s.tz.favorites);
  const lastSearchQuery    = useSelector((s) => s.tz.lastSearchQuery);
  const lastImageForReport = useSelector((s) => s.tz.lastImageForReport);
  const isReportGenerating = useSelector((s) => s.tz.reportGenerating);
  const reportError        = useSelector((s) => s.tz.reportError);

  const dispatch = useDispatch();
  const { generateReportOnServer } = useSocketContext();

  // локальное
  const [favorites, setFavorites]         = useState(reduxFavorites);
  const [selected,  setSelected]          = useState(null);
  const [preview,   setPreview]           = useState(lastImageForReport);
  const [reportQuery, setReportQuery]     = useState(lastSearchQuery);

  useEffect(() => setFavorites(reduxFavorites), [reduxFavorites]);
  useEffect(() => setReportQuery(lastSearchQuery), [lastSearchQuery]);

  // helpers ────────────────────────────────────────────────────────────────
  const getStatus = (doc = {}) =>
    String(getDocumentStatusDisplay(doc)).toLowerCase();

  const mktuNums = (doc = {}) => {
    const rawClasses =
      doc?.['511_Классы_МКТУ_и_перечень_товаров_и/или_услуг']
      || doc?.['511_KlassyMKTU']
      || doc?.['Nice Cl.']
      || doc?.['MARKGR/CURRENT/BASICGS@NICEVER'];
    const mktuList = rawClasses ? Array.from(new Set(String(rawClasses).match(/\d+/g))) : null;
    const mktuShort =
      mktuList && mktuList.length > 3 ? `${mktuList.slice(0, 3).join(', ')}…` : mktuList?.join(', ') || '—';
    return mktuShort ? (String(mktuShort).match(/\d+/g) || []) : [];
  };

  // filters
  const statusFilters = Array.from(
    new Set(favorites.map((f) => getStatus(f.document).split(' ')[0]).filter(Boolean)),
  ).map((v) => ({ text: v, value: v }));

  const mktuSet = new Set();
  favorites.forEach((f) => mktuNums(f.document).forEach((n) => mktuSet.add(n)));
  const mktuFilters = Array.from(mktuSet).map((n) => ({ text: n, value: n }));

  const queryFilters = Array.from(
    new Set(favorites.map((f) => f.searchQuery).filter(Boolean)),
  ).map((q) => ({ text: q, value: q }));

  // мастер-чекбокс
  const allSelected = favorites.length && favorites.every((f) => f.isInteresting);
  const toggleAll = ({ target: { checked } }) => {
    favorites.forEach((f) => {
      if (f.isInteresting !== checked) {
        dispatch(setFavoriteInteresting({ id: f.id, value: checked }));
      }
    });
  };

  // отчёт
  const handleReport = () => {
    const chosen = favorites.filter((f) => f.isInteresting);
    
    if (!chosen.length) {
      return message.warning('Нет выбранных записей.');
    }

    // Валидация данных перед отправкой
    const validationErrors = [];
    
    chosen.forEach((item, index) => {
      const errors = [];
      
      // Проверяем наличие документа
      if (!item.document) {
        errors.push('отсутствуют данные документа');
      } else {
        // Проверяем обязательные поля документа в зависимости от типа
        if (item.request_type === 'wipo') {
          // Для WIPO проверяем наличие Reg. No или @INTREGN
          if (!item.document['Reg. No'] && !item.document['@INTREGN'] && !item.document.Trademark) {
            errors.push('отсутствует регистрационный номер или название для WIPO');
          }
        } else {
          // Для ТЗ и заявок проверяем стандартные поля
          if (!item.document.realName && !item.document['111_numReg'] && !item.document['210_Номер_заявки']) {
            errors.push('отсутствует название или номер');
          }
        }
      }
      
      // Проверяем тип запроса
      if (!item.request_type) {
        errors.push('не указан тип запроса');
      }
      
      if (errors.length > 0) {
        validationErrors.push(`Запись ${index + 1}: ${errors.join(', ')}`);
      }
    });
    
    if (validationErrors.length > 0) {
      message.error(`Недостаточно данных для отчета:\n${validationErrors.join('\n')}`);
      return;
    }

    const finalSearchQuery = (reportQuery ?? '').trim();

    const reportData = {
      trademarks: chosen.filter((f) => f.request_type !== 'requests' && f.request_type !== 'wipo'),
      requests:   chosen.filter((f) => f.request_type === 'requests'),
      wipo:       chosen.filter((f) => f.request_type === 'wipo'),
      searchQuery: finalSearchQuery,
      image: preview,
    };
    // persist last query
    dispatch(setLastSearchQueryAction(finalSearchQuery));

    // set loading and clear previous error
    dispatch(setReportGenerating(true));
    dispatch(setReportError(null));

    generateReportOnServer(reportData, (res) => {
      dispatch(setReportGenerating(false));
      if (!res || res.status !== 'ok') {
        const errMsg = res?.message || 'Ошибка генерации отчёта';
        dispatch(setReportError(errMsg));
        return message.error(errMsg);
      }
      
      try {
        const bytes = Uint8Array.from(atob(res.file), (c) => c.charCodeAt(0));
        const blob  = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const url   = URL.createObjectURL(blob);
        const a     = Object.assign(document.createElement('a'), {
          href: url,
          download: `Отчет_${new Date().toISOString().slice(0, 10)}.docx`,
        });
        a.click();
        URL.revokeObjectURL(url);
        message.success('Отчёт сформирован!');
      } catch (e) {
        console.error('Ошибка при скачивании файла:', e);
        const errMsg = 'Не удалось скачать файл.';
        dispatch(setReportError(errMsg));
        message.error(errMsg);
      }
    });
  };

  // DnD reorder
  const onDragEnd = (res) => {
    if (!res.destination) return;
    const list = Array.from(favorites);
    const [moved] = list.splice(res.source.index, 1);
    list.splice(res.destination.index, 0, moved);
    setFavorites(list);
  };

  // upload
  const handleUpload = ({ fileList }) => {
    if (!fileList.length) {
      setPreview(null);
      return dispatch(setLastImageForReport(null));
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      dispatch(setLastImageForReport(e.target.result));
    };
    reader.readAsDataURL(fileList[fileList.length - 1].originFileObj);
  };

  // таблица
  const data = favorites.map((f, i) => {
    // Для заявок score может быть в document.score или в f.score
    const itemScore = f.score !== undefined && f.score !== null ? f.score :
                     (f.document?.score !== undefined && f.document?.score !== null ? f.document.score : null);
    
    const item = {
      key: f.id,
      idx: i + 1,
      type: f.request_type,
      query: f.searchQuery,
      status: getStatus(f.document),
      mktu: mktuNums(f.document).join(', '),
      score: itemScore,
      card: f,
    };
    return item;
  });

  const columns = [
    {
      title: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox checked={allSelected} onChange={toggleAll} />
          <span style={{ marginLeft: 8 }}>№</span>
        </div>
      ),
      dataIndex: 'idx',
      width: 70,
      render: (t, r) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: 8 }}>{t}</span>
          <Checkbox
            checked={r.card.isInteresting}
            onChange={(e) => {
              dispatch(setFavoriteInteresting({ id: r.card.id, value: e.target.checked }));
            }}
          />
        </div>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      width: 110,
      render: (v) => (v === 'requests' ? 'Заявка' : v === 'wipo' ? 'WIPO' : 'ТЗ'),
      filters: [
        { text: 'ТЗ', value: 'documents' },
        { text: 'Заявка', value: 'requests' },
        { text: 'WIPO', value: 'wipo' },
      ],
      onFilter: (v, r) => r.type === v,
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    {
      title: 'Поисковой запрос',
      dataIndex: 'query',
      ellipsis: true,
      sorter: (a, b) => (a.query || '').localeCompare(b.query || ''),
      filters: queryFilters,
      onFilter: (v, r) => r.query === v,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      sorter: (a, b) => a.status.localeCompare(b.status),
      filters: statusFilters,
      onFilter: (v, r) => r.status.split(' ')[0] === v,
    },
    {
      title: 'Классы МКТУ',
      dataIndex: 'mktu',
      sorter: (a, b) => a.mktu.localeCompare(b.mktu),
      filters: mktuFilters,
      onFilter: (v, r) => r.mktu.split(', ').includes(String(v)),
    },
    {
      title: 'Степень сходства',
      dataIndex: 'score',
      width: 120,
      sorter: (a, b) => {
        const scoreA = a.score !== null && a.score !== undefined ? Number(a.score) : -Infinity;
        const scoreB = b.score !== null && b.score !== undefined ? Number(b.score) : -Infinity;
        return scoreA - scoreB;
      },
      render: (score) => {
        if (score === null || score === undefined) {
          return <span style={{ color: '#999' }}>—</span>;
        }
        const scoreValue = Number(score);
        return (
          <div style={{
            fontWeight: 'bold',
            color: scoreValue <= 1 ? '#52c41a' :
                   scoreValue < 8 ? '#faad14' :
                   scoreValue < 13 ? '#fa8c16' : '#f5222d'
          }}>
            {scoreValue.toFixed(2)}
          </div>
        );
      },
    },
    {
      title: 'Карточка',
      dataIndex: 'card',
      render: (c) => (
        <FavoriteCard
          document={c.document}
          imgSrc={c.imgSrc}
          searchQuery={c.searchQuery}
          score={c.score}
          compact={stackReportToolbar}
          onClick={() => setSelected(c)}
        />
      ),
    },
  ];

  // ─── render ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* шапка */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'flex-start',
          flexDirection: stackReportToolbar ? 'column' : 'row',
          marginBottom: 16,
        }}
      >
        <Input
          placeholder="Поисковой запрос"
          value={reportQuery}
          onChange={(e) => setReportQuery(e.target.value)}
          style={{
            width: stackReportToolbar ? '100%' : REPORT_INPUT_WIDTH,
            maxWidth: '100%',
            minWidth: 0,
          }}
        />
        <div style={{ flexShrink: 0, minWidth: 0 }}>
          <Upload
            beforeUpload={() => false}
            onChange={handleUpload}
            onRemove={() => { setPreview(null); dispatch(setLastImageForReport(null)); }}
            listType="picture-card"
            maxCount={1}
            accept="image/*"
            showUploadList={false}
          >
            {preview ? (
              <div style={{ position: 'relative', width: 100, height: 100 }}>
                <img
                  src={preview}
                  alt="Превью изображения для отчёта"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={(e) => { e.stopPropagation(); setPreview(null); }}
                  style={{ position: 'absolute', top: 0, right: 0 }}
                />
              </div>
            ) : (
              <div style={{ textAlign: 'center' }}>
                <UploadOutlined style={{ fontSize: 24 }} />
                <div>Выбрать</div>
              </div>
            )}
          </Upload>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Button
            type="primary"
            block={stackReportToolbar}
            onClick={handleReport}
            loading={isReportGenerating}
            disabled={isReportGenerating}
          >
            Сформировать отчёт
          </Button>
        </div>
      </div>

      {reportError && (
        <Row style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Alert
              type="error"
              message="Ошибка формирования отчёта"
              description={reportError}
              closable
              onClose={() => dispatch(setReportError(null))}
              showIcon
            />
          </Col>
        </Row>
      )}

      {/* таблица с DnD: белый фон под горизонтальным скроллом, без «просвета» фона страницы */}
      <div className="favorites-table-wrap">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="fav" direction="vertical">
            {(prov) => (
              <div ref={prov.innerRef} {...prov.droppableProps}>
                <Table
                  className="favorites-table"
                  size={stackReportToolbar ? 'small' : 'middle'}
                  scroll={{ x: 'max-content' }}
                  dataSource={data}
                  columns={columns}
                  pagination={false}
                  rowKey="key"
                  onRow={(rec, i) => ({ index: i, 'data-row-key': rec.key })}
                  components={{ body: { row: DraggableBodyRow } }}
                />
                {prov.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* модалка */}
      {selected && (
        selected.request_type === 'requests' ? (
          <RequestModal
            open
            onClose={() => setSelected(null)}
            doc={selected}
            imgSrc={selected.imgSrc}
          />
        ) : selected.request_type === 'wipo' ? (
          <WipoModal
            open
            onClose={() => setSelected(null)}
            data={selected.document}
            imgSrc={selected.imgSrc}
          />
        ) : (
          <DocumentModal
            open
            onClose={() => setSelected(null)}
            doc={selected}
            imgSrc={selected.imgSrc}
          />
        )
      )}
    </div>
  );
};

export default FavoritesTab;
